import { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Divider,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";

const PdfToExcel = () => {
	const [files, setFiles] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [lastMode, setLastMode] = useState(null); // "standard" | "ai"

	useEffect(() => {
		if (!loading) return undefined;
		const handleBeforeUnload = (e) => {
			e.preventDefault();
			e.returnValue = "";
		};
		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [loading]);

	const fileSummary = useMemo(() => {
		if (!files.length) return "No files selected";
		const names = Array.from(files).map((f) => f.name).join(", ");
		const totalKb = Math.round(
			Array.from(files).reduce((acc, f) => acc + (f.size || 0), 0) / 1024
		);
		return `${files.length} PDF(s), ~${totalKb} KB total — ${names}`;
	}, [files]);

	const onPickFiles = (e) => {
		setError("");
		setLastMode(null);
		const list = Array.from(e.target.files || []);
		if (!list.length) { setFiles([]); return; }
		const nonPdf = list.filter((f) => !String(f.name || "").toLowerCase().endsWith(".pdf"));
		if (nonPdf.length) {
			setFiles([]);
			setError("Only PDF files (.pdf) are allowed.");
			return;
		}
		setFiles(list);
	};

	const downloadBlob = (blob, filename) => {
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		a.remove();
		window.URL.revokeObjectURL(url);
	};

	const convert = async (useAi) => {
		if (!files.length) { setError("Select at least one PDF"); return; }
		setLoading(true);
		setError("");
		setLastMode(useAi ? "ai" : "standard");
		try {
			const form = new FormData();
			for (let i = 0; i < files.length; i += 1) {
				form.append("files", files[i]);
			}

			const endpoint = useAi ? "/reports/pdf-to-excel/ai" : "/reports/pdf-to-excel";
			const res = await pythonVmInstance.post(endpoint, form, {
				headers: { "Content-Type": "multipart/form-data" },
				responseType: "blob",
			});

			const contentDisposition = res.headers?.["content-disposition"] || "";
			const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
			const filename = match?.[1] || (useAi ? "pdf_extract_ai.xlsx" : "pdf_extract.xlsx");
			downloadBlob(res.data, filename);
		} catch (err) {
			let msg = err?.message || "Conversion failed";
			if (err?.response?.data instanceof Blob) {
				try {
					const text = await err.response.data.text();
					const parsed = JSON.parse(text);
					msg = parsed?.message || parsed?.error || text || msg;
				} catch {
					msg = "Conversion failed";
				}
			} else if (err?.response?.data) {
				msg = err.response.data?.message || err.response.data?.error || String(msg);
			}
			setError(String(msg));
		} finally {
			setLoading(false);
		}
	};

	return (
		<Box sx={{ p: 2 }}>
			<Typography variant="h6" sx={{ mb: 0.5 }}>
				Line Sheet Convertor
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				Upload one or more line sheet PDFs and download a combined Excel with product fields and images.
			</Typography>

			<Paper sx={{ p: 2 }}>
				{/* File picker row */}
				<Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
					<Button
						variant="outlined"
						component="label"
						startIcon={<UploadFileRoundedIcon />}
						disabled={loading}>
						Choose PDF(s)
						<input type="file" hidden accept="application/pdf" multiple onChange={onPickFiles} />
					</Button>
					<Typography variant="body2" sx={{ flex: 1, minWidth: 200 }}>
						{fileSummary}
					</Typography>
				</Stack>

				<Divider sx={{ my: 2 }} />

				{/* Two action buttons */}
				<Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
					{/* Standard — regex parser only */}
					<Box sx={{ flex: 1, minWidth: 220 }}>
						<Button
							fullWidth
							variant="outlined"
							onClick={() => convert(false)}
							disabled={loading || !files.length}
							startIcon={loading && lastMode === "standard" ? <CircularProgress size={16} /> : <DownloadRoundedIcon />}>
							{loading && lastMode === "standard" ? "Converting…" : "Convert (Standard)"}
						</Button>
						<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
							Fast. Uses embedded PDF text only. Some fields may be blank if text is not extractable.
						</Typography>
					</Box>

					{/* AI — Gemini multimodal */}
					<Box sx={{ flex: 1, minWidth: 220 }}>
						<Button
							fullWidth
							variant="contained"
							color="secondary"
							onClick={() => convert(true)}
							disabled={loading || !files.length}
							startIcon={loading && lastMode === "ai" ? <CircularProgress size={16} /> : <AutoAwesomeRoundedIcon />}>
							{loading && lastMode === "ai" ? "AI Processing…" : "Convert with AI"}
						</Button>
						<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
							Slower. Gemini reads each product image visually — best for image-heavy or design PDFs.
						</Typography>
					</Box>
				</Stack>

				{loading ? (
					<Alert severity="warning" sx={{ mt: 2 }}>
						{lastMode === "ai"
							? "AI conversion in progress — Gemini is processing each product image. This may take a minute. Do not close or refresh."
							: "Conversion in progress — do not close this tab or refresh."}
					</Alert>
				) : null}

				{!loading && lastMode ? (
					<Alert severity="success" sx={{ mt: 2 }}>
						Done! Downloaded using{" "}
						<Chip
							size="small"
							label={lastMode === "ai" ? "AI (Gemini)" : "Standard"}
							color={lastMode === "ai" ? "secondary" : "default"}
						/>{" "}
						mode.
					</Alert>
				) : null}

				{error ? (
					<Typography color="error" sx={{ mt: 2 }}>
						{error}
					</Typography>
				) : null}
			</Paper>
		</Box>
	);
};

export default PdfToExcel;
